import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Bike, Courier } from '@/services/types';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { tariffs } from '@/pages/courier/tariff.constants';
import { Input } from '@/components/ui/input';
import { Search, User, Phone } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Checkbox } from '@/components/ui/checkbox';

interface AddRentalFormProps {
  couriers: Courier[];
  bikes: Bike[];
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
}

export const AddRentalForm: React.FC<AddRentalFormProps> = ({
  couriers,
  bikes,
  onSubmit,
  loading
}) => {
  const [selectedTariff, setSelectedTariff] = useState(tariffs[0]);
  const [cost, setCost] = useState<string>(selectedTariff.price.toString());
  const [filteredCouriers, setFilteredCouriers] = useState<Courier[]>(couriers);
  const [courierSearch, setCourierSearch] = useState<string>('');
  const [selectedCourier, setSelectedCourier] = useState<string>('');
  const [selectedCourierDisplay, setSelectedCourierDisplay] = useState<string>('');
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [isVip, setIsVip] = useState<boolean>(false);
  const isMobile = useIsMobile();
  
  // Обновляем стоимость при изменении тарифа
  useEffect(() => {
    setCost(selectedTariff.price.toString());
  }, [selectedTariff]);

  // Фильтрация курьеров по поисковому запросу (имя и телефон)
  useEffect(() => {
    if (!courierSearch.trim()) {
      setFilteredCouriers(couriers);
      return;
    }
    
    const searchTerm = courierSearch.toLowerCase();
    
    const filtered = couriers.filter(courier => 
      (courier.name && courier.name.toLowerCase().includes(searchTerm)) || 
      (courier.phone && courier.phone.toLowerCase().includes(searchTerm))
    );
    setFilteredCouriers(filtered);
  }, [courierSearch, couriers]);

  // Обработчик выбора курьера из списка
  const handleCourierSelect = (courierId: string) => {
    setSelectedCourier(courierId);
    const selected = couriers.find(c => c.id === courierId);
    if (selected) {
      const displayName = selected.name || 'Без имени';
      const displayPhone = selected.phone || 'Нет номера';
      setSelectedCourierDisplay(`${displayName} (${displayPhone})`);
      setCourierSearch('');
    }
    setIsSearchFocused(false);
  };

  // Форматирование номера телефона для отображения
  const formatPhone = (phone: string | null): string => {
    if (!phone) return 'Нет номера';
    return phone;
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-4">
        <div className={cn(
          "grid items-center gap-2",
          isMobile ? "grid-cols-1" : "grid-cols-4"
        )}>
          <Label htmlFor="courier" className={isMobile ? "" : "text-right"}>
            Курьер
          </Label>
          <div className={cn(
            "relative",
            isMobile ? "col-span-1" : "col-span-3"
          )}>
            <div className="relative">
              {selectedCourierDisplay ? (
                <div className="flex items-center justify-between p-2 border rounded-md bg-secondary dark:bg-gray-800">
                  <div className="flex items-center space-x-2">
                    <User size={16} className="text-muted-foreground" />
                    <span className="text-foreground">{selectedCourierDisplay}</span>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => {
                      setSelectedCourier('');
                      setSelectedCourierDisplay('');
                      setCourierSearch('');
                      setTimeout(() => {
                        const searchInput = document.getElementById('courier-search');
                        if (searchInput) searchInput.focus();
                      }, 0);
                    }}
                    className="h-8 px-2 text-muted-foreground"
                  >
                    Изменить
                  </Button>
                </div>
              ) : (
                <div className="flex items-center relative">
                  <div className="relative w-full">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="courier-search"
                      placeholder="Поиск по имени или телефону..."
                      value={courierSearch}
                      onChange={(e) => setCourierSearch(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => {
                        // Используем setTimeout чтобы дать время для клика по результату поиска
                        setTimeout(() => {
                          if (!selectedCourier) {
                            setIsSearchFocused(false);
                          }
                        }, 200);
                      }}
                      className="pl-8 w-full"
                    />
                  </div>
                </div>
              )}

              {isSearchFocused && (
                <div className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white dark:bg-gray-800 shadow-lg">
                  {filteredCouriers.length > 0 ? (
                    filteredCouriers.map((courier) => (
                      <div 
                        key={courier.id} 
                        className="cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center dark:text-white"
                        onClick={() => handleCourierSelect(courier.id)}
                      >
                        <div className="flex items-center space-x-2">
                          <User size={16} className="text-gray-500 dark:text-gray-300" />
                          <span className="font-medium">{courier.name || 'Без имени'}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-300">
                          <Phone size={14} />
                          <span>{formatPhone(courier.phone)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 text-sm text-center text-gray-500 dark:text-gray-300">Нет курьеров по запросу</div>
                  )}
                </div>
              )}

              <input 
                type="hidden" 
                name="courier" 
                value={selectedCourier} 
                required
              />
            </div>
          </div>
        </div>
        
        <div className={cn(
          "grid items-start gap-2",
          isMobile ? "grid-cols-1" : "grid-cols-4"
        )}>
          <Label className={isMobile ? "" : "text-right pt-2"}>
            Тариф
          </Label>
          <div className={isMobile ? "col-span-1" : "col-span-3"}>
            <RadioGroup 
              defaultValue={selectedTariff.key} 
              className="space-y-3"
              name="tariffType"
              onValueChange={(value) => {
                const newTariff = tariffs.find(t => t.key === value);
                if (newTariff) {
                  setSelectedTariff(newTariff);
                }
              }}
            >
              {tariffs.map((tariff) => (
                <div key={tariff.key} className="flex items-center space-x-2 border p-3 rounded-md hover:bg-muted/50">
                  <RadioGroupItem value={tariff.key} id={`tariff-${tariff.key}`} />
                  <Label htmlFor={`tariff-${tariff.key}`} className="flex flex-col cursor-pointer w-full">
                    <span className="font-medium">{tariff.title}</span>
                    <span className="text-muted-foreground text-sm">{tariff.price} ₽ / {tariff.period}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
        
        <div className={cn(
          "grid items-center gap-2",
          isMobile ? "grid-cols-1" : "grid-cols-4"
        )}>
          <Label htmlFor="isVip" className={isMobile ? "" : "text-right"}>
            VIP-статус
          </Label>
          <div className={cn(
            "flex items-center space-x-2",
            isMobile ? "col-span-1" : "col-span-3"
          )}>
            <Checkbox 
              id="isVip" 
              name="isVip" 
              value="yes"
              checked={isVip} 
              onCheckedChange={(checked) => setIsVip(!!checked)} 
            />
            <span className="text-sm text-muted-foreground">
              Пометить как VIP-клиента (номер заявки будет начинаться с VIP-)
            </span>
          </div>
        </div>
        
        <div className={cn(
          "grid items-center gap-2",
          isMobile ? "grid-cols-1" : "grid-cols-4"
        )}>
          <Label htmlFor="cost" className={isMobile ? "" : "text-right"}>
            Стоимость (₽)
          </Label>
          <Input
            id="cost"
            name="cost"
            type="number"
            className={isMobile ? "col-span-1" : "col-span-3"}
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            required
          />
        </div>

        {/* Скрытые поля для передачи данных в форме */}
        <input type="hidden" name="tariffKey" value={selectedTariff.key} />
        <input type="hidden" name="tariffTitle" value={selectedTariff.title} />
        <input type="hidden" name="startDate" value={new Date().toISOString()} />
        <input type="hidden" name="isVip" value={isVip ? 'yes' : 'no'} />
      </div>
      <div className="flex justify-end mt-6">
        <Button 
          type="submit" 
          disabled={loading || !selectedCourier} 
          className={cn(isMobile && "w-full")}
        >
          {loading ? 'Создание...' : 'Оформить'}
        </Button>
      </div>
    </form>
  );
};
